/**
 * @description Component-TradeModeSwitch - Trading mode switch button component
 */
import { useAgentStore } from '@/features/agent/store';
import './index.less';

import { Button } from '@/components/Button';
import { TradingMode } from '@/types/agent';
import { FC, useCallback } from 'react';
import { useAgentAction } from '../hook';

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  onModeChange?: (mode: TradingMode) => void;
}

/**
 * Trading mode switch button component with Classic and Express + One-Click modes
 */
const TradeModeSwitch: FC<IPropTypes> = function ({ className = '' }) {
  const { tradingMode, setIsTradingModeChanging, isTradingModeChanging } = useAgentStore();
  const { tryEnableAgent, tryDisableAgent } = useAgentAction();

  // Handle mode toggle
  const handleModeToggle = useCallback(async () => {
    if (isTradingModeChanging) return;

    setIsTradingModeChanging(true);

    try {
      if (tradingMode === TradingMode.Classic) {
        // Switch to Express + One-Click mode
        await tryEnableAgent();
      } else {
        // Switch to Classic mode
        await tryDisableAgent();
      }
    } catch (error) {
      console.error('Failed to toggle trading mode:', error);
    } finally {
      setIsTradingModeChanging(false);
    }
  }, [tradingMode, isTradingModeChanging, setIsTradingModeChanging, tryEnableAgent, tryDisableAgent]);

  const isExpressMode = tradingMode === TradingMode.Agent;
  const buttonText = isExpressMode ? 'Disable' : 'Enable';

  return (
    <div className={`trade-mode-switch ${className}`}>
      <div className="trade-mode-switch__description">Enable One-Click Mode</div>
      <Button
        type="outline"
        size="small"
        className={`trade-mode-switch__button ${
          isExpressMode ? 'trade-mode-switch__button--enabled' : 'trade-mode-switch__button--disabled'
        }`}
        onClick={handleModeToggle}
        disabled={isTradingModeChanging}>
        {isTradingModeChanging ? 'Switching...' : `${buttonText}`}
      </Button>
    </div>
  );
};

export default TradeModeSwitch;
